import React from 'react';
import ReactDOM from 'react-dom/client';
import PointSelector, { getSelectedPointId, clearSelectedPoint } from './PointSelector';
import { startServerSync, enableShiftSync, isWebMultiPointMode } from './serverSync';
import { applyPointStorage, loadPointState } from './pointStorage';
import { sanitizeReferencePayload } from './referenceDefaults';
import './index.css';

function LoadingScreen({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-100">
      <p className="text-neutral-600">{message || 'Загрузка точки…'}</p>
    </div>
  );
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <LoadingScreen
          message={`Ошибка приложения: ${this.state.error.message}. Обновите страницу или выберите точку заново.`}
        />
      );
    }
    return this.props.children;
  }
}

async function bootstrap() {
  const webMode = isWebMultiPointMode();
  const rootEl = document.getElementById('root');
  const root = ReactDOM.createRoot(rootEl);
  const pointId = getSelectedPointId();

  if (webMode && !pointId) {
    root.render(<PointSelector />);
    return;
  }

  if (webMode && pointId) {
    root.render(<LoadingScreen />);
    try {
      const state = await loadPointState(pointId);
      const sanitized = sanitizeReferencePayload(state);
      applyPointStorage(pointId, sanitized);
      const refFix = {};
      if (sanitized.products) refFix.products = sanitized.products;
      if (sanitized.consumablesItems) refFix.consumablesItems = sanitized.consumablesItems;
      if (Object.keys(refFix).length) {
        fetch(`/api/points/${pointId}/state`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...refFix, _pointId: pointId }),
        }).catch(console.warn);
      }
      enableShiftSync();
    } catch (e) {
      console.error(e);
      const stalePoint = e?.status === 404 || String(e?.message || '').includes('404');
      if (stalePoint) {
        clearSelectedPoint();
        root.render(<PointSelector />);
        return;
      }
      root.render(
        <LoadingScreen message="Не удалось загрузить данные точки с сервера. Проверьте соединение и обновите страницу." />
      );
      return;
    }
  }

  if (webMode && pointId) {
    startServerSync();
  }

  const { default: App } = await import('./App');

  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>
  );
}

bootstrap().catch((e) => {
  console.error(e);
  const rootEl = document.getElementById('root');
  if (!rootEl) return;
  const root = ReactDOM.createRoot(rootEl);
  root.render(<LoadingScreen message={`Критическая ошибка загрузки: ${e.message}`} />);
});
